import { useMemo, forwardRef, useRef } from 'react';

import { useVerticalDnD } from 'hooks/dnd';

import Tooltip from 'components/tooltip';
import DraggableItem from 'components/DraggableItem';

import IconMoveHandle from 'icons/icon-move-handle';

import './ResourceListItem.styles.css';

interface ResourceListItemProps {
    id: string,
    index?: boolean,
    isNew?: boolean,
    canEdit?: boolean,
    editing?: boolean,
    focused?: boolean,
    completed?: boolean,
    draggable?: boolean,
    dragType?: string,
    noGutter?: boolean,
    markerElement?: React.ReactNode,
    onMarkerClick?: (event: React.MouseEvent<HTMLDivElement>) => void,
    footElement?: React.ReactNode,
    getActionItems?: (id: string) => React.ReactNode[],
    getMetaItems?: (id: string) => {
        icon?: React.ReactNode,
        content?: React.ReactNode,
        isAction?: boolean
        tooltip?: React.ReactNode,
        pushRight?: boolean
    }[],
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void,
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>) => void,
    moveItem?: (dragId: string, dropId: string, isDrop: boolean) => void,
    style?: React.CSSProperties,
    children: React.ReactNode
};

const ResourceListItem = forwardRef<HTMLDivElement, ResourceListItemProps>(({ id, index, isNew, canEdit, editing, focused, completed, draggable, dragType, noGutter, markerElement, onMarkerClick, footElement, getActionItems, getMetaItems, onClick, onDoubleClick, moveItem, style, children }: ResourceListItemProps, ref) => {
    const classNames = [];

    const elementRef = useRef<HTMLDivElement | null>(null);

    if (isNew) classNames.push('is-new');
    if (canEdit) classNames.push('can-edit');
    if (editing) classNames.push('editing');
    if (focused) classNames.push('focus');
    if (completed) classNames.push('completed');
    if (noGutter) classNames.push('no-gutter');

    const { drag, drop, preview, isDragging, isOver } = useVerticalDnD({
        element: elementRef,
        type: `RESOURCE_LIST_ITEM_${dragType}`,
        id,
        index,
        moveItem,
        enabled: draggable
    });

    const actionItems = useMemo(() => {
        return getActionItems?.(id) || [];
    }, [getActionItems]);

    const metaItems = useMemo(() => {
        return getMetaItems?.(id) || [];
    }, [getMetaItems]);


    const setRef = (el: HTMLDivElement | null) => {
        if (ref) {
            if (typeof ref === 'function') {
                ref(el);
            }

            if ('current' in ref) {
                ref.current = el;
            }
        }

        drop(el);
        drag(el);
        elementRef.current = el;
    }

    return (
        <DraggableItem ref={setRef} isDragging={isDragging} isOver={isOver} className={'resource-list-item ' + classNames.join(' ')} style={style}>
            <div className="item-click-overlay" onClick={onClick} onDoubleClick={onDoubleClick} />

            {
                draggable ? (
                    <div className="item-sort-handle">
                        <IconMoveHandle />
                    </div>
                ) : null
            }

            <div className="item-main">
                {
                    markerElement ? (
                        <div className="item-head">
                            <div className="item-marker item-click-action" onClick={onMarkerClick}>
                                { markerElement }
                            </div>
                        </div>
                    ) : null
                }

                <div className="item-body">
                    <div className="item-title-holder">
                        { children }
                    </div>

                    {
                        actionItems.length ? (
                            <div className="item-actions">
                                {
                                    actionItems.map((actionElement, index) => <div className="action item-click-action" key={index}>{ actionElement }</div>)
                                }
                            </div>
                        ) : null
                    }
                </div>

                {
                    footElement ? (
                        <div className="item-foot">
                            { footElement }
                        </div>
                    ) : null
                }
            </div>

            {
                metaItems.length ? (
                    <div className={'item-meta' + (markerElement ? ' push-inside' : '')}>
                        {
                            metaItems.map(({ icon, content, isAction, tooltip, pushRight }, index) => (
                                <Tooltip disabled={!tooltip} tooltip={tooltip}>
                                    <div className={'meta-item' + (isAction || tooltip ? ' item-click-action' : '') + (pushRight ? ' push-right' : '')} key={index}>
                                        { icon ? <div className="meta-item-icon">{ icon }</div> : null }
                                        { content ? <div className="meta-item-body">{ content }</div> : null }
                                    </div>
                                </Tooltip>
                            ))
                        }
                    </div>
                ) : null
            }
        </DraggableItem>
    );
});

export default ResourceListItem;
